import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ApiService from '../services/api';
import type { NavVisibilityInput } from '../constants/appNavigation';
import { isEnterpriseAppPath } from '../constants/appNavigation';
import { getCurrentUserRole } from '../utils/authRole';
import { hasMsmeExportIntent } from '../utils/msmeExportIntent';
import { useMsmeProfileQuery } from './queries/useMsmeWorkspaceQueries';
import { myCompanyPagesComplete } from '../utils/myCompanyRequired';

/**
 * Sidebar unlock state. Do NOT refetch on every pathname change — unknown URLs
 * (e.g. /This) previously re-ran this hook, failed the profile fetch, and locked
 * the whole workspace until a hard reload (CI-BUG-015).
 */
export function useNavVisibility(): NavVisibilityInput & { loading: boolean } {
  const location = useLocation();
  const enterpriseMode =
    isEnterpriseAppPath(location.pathname) || getCurrentUserRole() === 'enterprise';
  const [input, setInput] = useState<NavVisibilityInput>({
    hasProfile: false,
    hasAssessment: false,
    hasExportIntent: false,
  });
  const [loading, setLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const profileQuery = useMsmeProfileQuery(!enterpriseMode);
  const msmeProfile = profileQuery.data?.data;

  const load = useCallback(async (mode: boolean) => {
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    try {
      const profileRes = mode
        ? await ApiService.getEnterpriseProfile()
        : await ApiService.getMSMEProfile();
      const profile = profileRes?.data;
      let hasAssessment = false;
      try {
        if (mode) {
          const workflowRes = await ApiService.getEnterpriseWorkflow().catch(() => null);
          const sections = (workflowRes?.data?.sections || []) as Array<{ status?: string }>;
          hasAssessment = sections.some((section) => section.status === 'completed')
            || Boolean((profile as { facilities?: unknown[] })?.facilities?.length);
        } else {
          const assessRes = await ApiService.getCarbonAssessment({ limit: 1 });
          hasAssessment = (assessRes?.data?.assessments?.length || assessRes?.data?.length || 0) > 0;
        }
      } catch {
        hasAssessment = false;
      }
      setInput({
        hasProfile: Boolean(profile?.companyName),
        hasAssessment,
        hasExportIntent: hasMsmeExportIntent(profile as Parameters<typeof hasMsmeExportIntent>[0]),
      });
      hasLoadedOnceRef.current = true;
    } catch (error) {
      const status = Number((error as { response?: { status?: number }; status?: number })?.response?.status
        ?? (error as { status?: number })?.status
        ?? 0);
      const transient = !status || status >= 500;
      setInput((prev) => {
        if (hasLoadedOnceRef.current) return prev;
        if (transient) {
          // P4-BUG-001: a gateway timeout is not proof the company is missing.
          return { hasProfile: true, hasAssessment: true, hasExportIntent: prev.hasExportIntent };
        }
        return { hasProfile: false, hasAssessment: false, hasExportIntent: false };
      });
      if (transient) {
        hasLoadedOnceRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enterpriseMode) return;
    void load(true);
  }, [enterpriseMode, load]);

  useEffect(() => {
    if (enterpriseMode) return;
    let cancelled = false;
    const loadAssessment = async () => {
      if (!hasLoadedOnceRef.current) setLoading(true);
      try {
        const assessRes = await ApiService.getCarbonAssessment({ limit: 1 });
        if (cancelled) return;
        setInput((prev) => ({
          ...prev,
          hasAssessment: (assessRes?.data?.assessments?.length || assessRes?.data?.length || 0) > 0,
        }));
        hasLoadedOnceRef.current = true;
      } catch {
        if (!cancelled) {
          setInput((prev) => ({ ...prev, hasAssessment: prev.hasAssessment }));
          hasLoadedOnceRef.current = true;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadAssessment();
    return () => {
      cancelled = true;
    };
  }, [enterpriseMode]);

  if (!enterpriseMode) {
    const status = Number((profileQuery.error as { response?: { status?: number }; status?: number } | null)?.response?.status
      ?? (profileQuery.error as { status?: number } | null)?.status
      ?? 0);
    const transientProfileError = profileQuery.isError && (!status || status >= 500);
    return {
      hasProfile: transientProfileError
        ? true
        : (msmeProfile ? myCompanyPagesComplete(msmeProfile) : false),
      hasAssessment: input.hasAssessment,
      hasExportIntent: msmeProfile
        ? hasMsmeExportIntent(msmeProfile as Parameters<typeof hasMsmeExportIntent>[0])
        : false,
      loading: (profileQuery.isLoading && !msmeProfile) || loading,
    };
  }

  return { ...input, loading };
}
